import React, { useRef, useState, useEffect, useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import { useMount } from '@umijs/hooks';
import classnames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { history, useParams, useIntl } from 'umi';
import { Icon, Header, Modal, message, Tooltip, ErrorBrowserHeader, Button } from '@/components';
import ErrorBoundary from '@/components/Error';
import FeedbackPanelH5Drawer, { H5DraserRef } from '@/components/FeedbackPanel/H5Drawer';
import { MODE } from '@/models/meeting';
import { RootStore, barrage, focused, reaction, speakWeight } from '@/models';
import { checkBrowerResult } from '@/utils/deviceCheck';
import PeoplePanel from './component/PeoplePanel';
import ControlBar from './component/ControlBar';
import ChatPanel from './component/ChatPanel';
import TranscribePanel from './component/TranscribePanel';
import Style from './index.less';
import ViewPanel from './component/ViewPanel';
import Loading from './component/Loading';
import { useWaterMarker } from '@/utils/waterMarker';
import useStompResponse from '@/pages/Meeting/hooks/stompResponse';
import useRtcJoinChannel from '@/pages/Meeting/hooks/useJoinChannel';
import useLocalStreamVoice from '@/pages/Meeting/hooks/useLocalStreamVoice';
import useGlobalTips from './hooks/useGlobalTips';
import BarragePanel from './component/BarragePanel';
import { isEmpty } from 'lodash-es';
import { HandUpStatus } from './component/EmoJ';
import { useSpaceLongPress } from './hooks/useSpaceLongPress';
import { useOpenAudio } from './hooks/useOpenAudio';
import TempoaryUnmute from './component/TempoaryUnmute';
import { Header as PCHeader } from '@/components/PC/Header';
import SupportMessage from './component/ControlBar/SupportMessage';
interface MeetingProps
  extends Pick<RootStore, 'meeting' | 'user' | 'stomp' | 'chat' | 'members' | 'transcribe'> {
  RTCInstance: RootStore['rtc'];
  simultaneous: RootStore['simultaneousInterpretation'];
}
interface ParamsStruct {
  meetingId: string;
}

const Meeting: React.FC<MeetingProps> = inject(({ store }: { store: RootStore }) => {
  return {
    meeting: store.meeting,
    RTCInstance: store.rtc,
    user: store.user,
    stomp: store.stomp,
    chat: store.chat,
    transcribe: store.transcribe,
  };
})(
  observer((props) => {
    const { meeting, RTCInstance, user, stomp, chat, transcribe } = props;
    const { meetingId } = useParams<ParamsStruct>();
    const [changeVideoLoading, SetChangeVideoLoading] = React.useState(false);
    const { formatMessage } = useIntl();
    const [errBrowserShow, setErrBrowserShow] = useState(true);

    useWaterMarker();
    // stomp 长链接通知
    useStompResponse({ meetingId });
    // rtc 进入房间并且添加监听
    useRtcJoinChannel({ meetingId });

    // 本端声音大小采集
    useLocalStreamVoice();
    // 全局提示
    useGlobalTips();

    const feedbackDef = useRef<H5DraserRef>(null);

    const headerOperationNode = () => {
      return (
        <>
          {meeting.memberCount >= 2 &&
            !RTCInstance.breakLineReconnecting &&
            !meeting.screenUid &&
            !meeting.whiteboardUid &&
            !meeting.codeEditorEnabled &&
            !focused?.focusedYuxid && (
              <div className={Style['view-control-wrap']}>
                <Tooltip title={formatMessage({ id: 'PRESENTER_VIEW' })}>
                  <div
                    className={classnames(Style['lecturer-view'], {
                      [Style.actived]: meeting?.viewMode === MODE.LECTURER,
                    })}
                    onClick={() => {
                      meeting?.setViewMode(MODE.LECTURER, true);
                    }}
                  >
                    <div className={Style['view-icon']} />
                  </div>
                </Tooltip>

                <Tooltip title={formatMessage({ id: 'GRID_VIEW' })}>
                  <div
                    className={classnames(Style['grid-view'], {
                      [Style.actived]: meeting?.viewMode === MODE.GRID,
                    })}
                    onClick={() => {
                      meeting?.setViewMode(MODE.GRID, true);
                    }}
                  >
                    <Icon className={Style['view-icon']} name="icon-shitu_gongge2" size={20} />
                  </div>
                </Tooltip>
              </div>
            )}
          {focused?.focusedYuxid && (
            <Tooltip
              title={formatMessage({ id: 'spotLighted' })}
              placement="bottomRight"
              className={Style['look-at-tooltip']}
            >
              <div className={Style['focused-name']}>
                <Icon
                  name="icon-focus_solid_1"
                  size={20}
                  mode="symbol"
                  className={Style['focused-name-icon']}
                ></Icon>
                {meeting?.membersMap.get(focused?.focusedYuxid)?.memberName}
              </div>
            </Tooltip>
          )}

          <div className={classnames(Style['page-count-right'])}>
            <Tooltip title={formatMessage({ id: meeting.peoplePanelShow ? 'COLLAPSE' : 'EXPAND' })}>
              <div
                className={classnames(Style['people-count'], {
                  [Style.actived]: meeting.peoplePanelShow,
                  [Style.disable]: RTCInstance.breakLineReconnecting,
                })}
                onClick={() => {
                  if (RTCInstance.breakLineReconnecting) {
                    return;
                  }
                  meeting.setPeoplePanelShow(!meeting.peoplePanelShow);
                  chat.setChatVisible(false);
                  transcribe.setTranscribeVisible(false);
                }}
              >
                <Icon className={Style['people-icon']} name="icon-contact" size={20} />
                <span className={Style.count}>{meeting.memberCount}</span>
              </div>
            </Tooltip>
          </div>
        </>
      );
    };

    const getMemberHandUpStatus = () => {
      API.api.reaction.getHandUpDevices
        .request({ channelId: meeting?.info?.channelId })
        .then((res) => {
          if (!isEmpty(res.data)) {
            res.data.map((item) => {
              meeting?.updateMemberHandUpStatusYxuids([`${item.yxMeetingUid}`], HandUpStatus.on);
              meeting?.updateRectionMap(`${item.yxMeetingUid}`, {
                memberUid: item.yxMeetingUid,
                timestamp: Date.now(),
                isHandup: HandUpStatus.on,
              });
            });
          }
        });
    };

    const getMemberHandupSetting = () => {
      API.api.webSettingV3.getSetting.request({}).then((res) => {
        reaction?.setHandupSetting({
          handUpAnimWebp: `${res?.data?.reactionSetting?.handUpAnimWebp}?fop=imageView/2/w/128`,
          handUpAnimWebpDuration: res?.data?.reactionSetting?.handUpAnimWebpDuration,
          handUpStaticWebp: `${res?.data?.reactionSetting?.handUpStaticWebp}?fop=imageView/2/w/128`,
        });
        speakWeight.setSpeakDetectonSetting({
          ignoreCallbackRateLessThan:
            res.data.speakerDetectionSetting?.speakerEnterAlgorithmV1?.ignoreCallbackRateLessThan,
          ignoreVolumeLessThan:
            res.data.speakerDetectionSetting?.speakerEnterAlgorithmV1?.ignoreVolumeLessThan,
        });
      });
    };

    const changeVideo = () => {
      SetChangeVideoLoading(true);
      RTCInstance?.setVideoValueBack().finally(() => SetChangeVideoLoading(false));
    };

    // 计算viewBox区域的高度
    const topBoxBottom = useMemo(() => {
      if (barrage.barrageVisible) {
        return barrage.minHeight + barrage.mvHeight + 8;
      }
      return 0;
    }, [barrage.barrageVisible, barrage.mvHeight]);

    const leftMinHeight = useMemo(() => {
      if (barrage.barrageVisible) {
        return barrage.minHeight + barrage.mvHeight + 200;
      }
      return 432;
    }, [barrage.mvHeight, barrage.barrageVisible]);

    useEffect(() => {
      // 参会人大于两人且当前会议内没有开启屏幕共享时，显示宫格视图
      // 当前用户未操作时，按以下规则切换视图
      // todo: uid转换
      const isSharing = meeting.screenUid || meeting.whiteboardUid || meeting.codeEditorEnabled;
      if (window.isMobile) {
        if (isSharing) {
          meeting?.setViewMode(MODE.ONLYSHARE, false);
        } else if (!meeting?.userSelectedViewMode) {
          if (meeting.memberCount === 1) {
            meeting?.setViewMode(MODE.ONLY);
          }
          // 会议只有两人参会时，显示演讲者视图
          if (meeting.memberCount === 2) {
            meeting?.setViewMode(MODE.LECTURER);
          }
          if (meeting.memberCount > 2) {
            meeting?.setViewMode(MODE.GRID);
          }
        }
      } else if (focused?.focusedYuxid && user.yxuid && focused?.focusedYuxid !== user.yxuid) {
        meeting?.setViewMode(MODE.LECTURER, false);
      } else if (isSharing || meeting?.codeEditorEnabled) {
        meeting?.setViewMode(MODE.LECTURER, false);
      } else if (!meeting?.userSelectedViewMode) {
        meeting?.setViewMode(MODE.GRID);
      } else {
        meeting?.setViewMode(meeting?.userSelectedViewMode);
      }
    }, [
      meeting,
      meeting.memberCount,
      meeting.screenUid,
      meeting.whiteboardUid,
      meeting.codeEditorEnabled,
      focused?.focusedYuxid,
      user.yxuid,
    ]);

    useEffect(() => {
      if (RTCInstance.cid) {
        meeting.queryMemberInfoByYXUid(RTCInstance.cid, [Number(user?.yxuid)]);
        if (user.sessionId && user.uid) {
          stomp.connect(String(RTCInstance.cid), user.sessionId, user.uid);
        }
      }
    }, [RTCInstance.cid, user.sessionId, user.uid, user.yxuid, meeting, stomp]);

    useEffect(() => {
      return () => {
        stomp.disconnect();
      };
    }, [stomp]);

    useMount(() => {
      const fn = () => {
        if (RTCInstance && !RTCInstance?.audioIsPlaying) {
          [...RTCInstance.remoteStreamsMap.values()].forEach((stream: any) => {
            stream.play();
            if (window.isMobile || window.isPad) {
              document.body?.removeEventListener('touchstart', fn);
            } else {
              document.removeEventListener('mousedown', fn);
            }
          });
        }
      };
      if (window.isMobile || window.isPad) {
        document.body?.addEventListener('touchstart', fn, false);
      } else {
        document.addEventListener('mousedown', fn);
      }
      //获取举手资源
      getMemberHandupSetting();
    });

    useEffect(() => {
      // 获取参会设备举手状态
      if (meeting?.info?.channelId) {
        getMemberHandUpStatus();
      }
    }, [meeting?.info?.channelId]);
    const { openAudioSpace, closeAudioSpace } = useOpenAudio();
    // 处理键盘长按事件
    useSpaceLongPress(
      () => {
        if (!document.activeElement?.isSameNode(document.body)) {
          return;
        }

        if (!RTCInstance?.isOpenAudio) {
          openAudioSpace();
        }
      },
      () => {
        meeting.temporaryUnmute && closeAudioSpace();
      }
    );

    useEffect(() => {
      return () => {
        meeting.setSpeakerYxUid('');
      };
    }, []);

    return (
      <div className={Style['popo-meeting-page']}>
        <ErrorBrowserHeader
          show={errBrowserShow}
          onClose={() => setErrBrowserShow(false)}
          size={window.isMobile ? 'large' : 'small'}
        />
        {window.isMobile && (
          <div className={Style['top-bar']}>
            <FeedbackPanelH5Drawer ref={feedbackDef} type="meeting" />
            <div className={Style['bar-btn']} onClick={() => feedbackDef.current?.open()}>
              <Icon name="icon-feedback" size={24} className={Style.icon} />
            </div>
            {RTCInstance?.isOpenVideo && (
              <div className={classnames(Style['bar-btn'])} onClick={changeVideo}>
                <Icon
                  className={Style.icon}
                  name="icon-SWAP_solid"
                  size={24}
                  loading={changeVideoLoading}
                />
              </div>
            )}
            {checkBrowerResult && (
              <div className={Style['bar-btn']} onClick={() => setErrBrowserShow(true)}>
                <Icon name="iconexclamation_solid" size={24} style={{ color: '#FFBF40' }} />
              </div>
            )}
          </div>
        )}

        {/* <div style={{ display: 'flex' }}>
          <Button
            onClick={() => {
              meeting.setSpeakerYxUid('1850068');
              meeting.setSpeakerYxUid('1850068');
            }}
          >
            出现1
          </Button>
          <Button
            onClick={() => {
              meeting.setSpeakerYxUid('1940233');
              meeting.setSpeakerYxUid('1940233');
            }}
          >
            出现2
          </Button>
        </div> */}

        <ErrorBoundary>
          {window.isMobile ? (
            <Header
              title={meeting.title || ''}
              meetingNumber={meeting.info?.meetingId || Number(meetingId)}
              createdTime={meeting.createdTime}
              operationRender={headerOperationNode}
            />
          ) : (
            <PCHeader
              createdTime={meeting.createdTime}
              operationRender={headerOperationNode}
            ></PCHeader>
          )}

          <div className={Style.content}>
            {/* 音视频列表视图 */}
            <div className={Style.left} id="playerContainer" style={{ minHeight: leftMinHeight }}>
              <div className={Style.topBox} style={{ bottom: topBoxBottom }}>
                <ViewPanel className={Style['view-panel']} />
              </div>

              {/* 字幕视图 */}

              <div
                className={classnames(Style.barragePanel, {
                  hidden: !barrage.barrageVisible,
                })}
              >
                <BarragePanel visible={barrage.barrageVisible} />
              </div>
            </div>

            <CSSTransition
              in={chat.chatVisible || meeting.peoplePanelShow || transcribe.transcribeVisible}
              timeout={100}
              classNames={{
                enter: Style.unvisible,
                exitActive: Style.unvisible,
                exitDone: 'hidden',
              }}
            >
              <div
                id="siderContainer"
                className={classnames(Style['side-panel'], {
                  hidden:
                    !chat.chatVisible && !meeting.peoplePanelShow && !transcribe.transcribeVisible,
                })}
              >
                {/* 人员列表视图 */}
                <PeoplePanel
                  className={Style['people-panel']}
                  talkingIds={meeting?.speakerYxUidList}
                  visible={meeting.peoplePanelShow}
                />
                {/* 聊天列表视图 */}
                <ChatPanel
                  talkingIds={meeting?.speakerYxUidList}
                  visible={chat.chatVisible && chat.isAbleUse}
                />
                {/* 转录视图 */}
                <TranscribePanel visible={transcribe.transcribeVisible} />
              </div>
            </CSSTransition>
          </div>
          {/* 底部操作按钮 */}
          <ControlBar />
          <SupportMessage className={Style.supportMessage} />
        </ErrorBoundary>
        {(window.isPC || window.isWeb) &&
          (meeting.whiteboardLoading || meeting.codeEditorLoading) && (
            <Loading type={meeting.whiteboardLoading ? 'whiteboard' : 'code'} />
          )}

        <TempoaryUnmute></TempoaryUnmute>
      </div>
    );
  })
);

export default Meeting;
